import { useEffect, useState } from 'react';
import { useQueryClient } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';

import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { fetchTerminals } from 'api/terminals/terminals';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useMutation } from 'hooks/useMutation/useMutation';
import { WantedTerminalsIdSortEnum } from 'context/query/queryContext/QueryContext.enum';
import { useModalState } from 'hooks/useModalState/useModalState';
import { addWantedTerminals } from 'api/wanted/wanted';
import { fetchServers } from 'api/servers/servers';

import { useAddWantedTerminalsValidation } from './AddWantedTerminals.validation';
import { AddWantedTerminalsFormBody, AddWantedTerminalsContainerProps } from './AddWantedTerminals.types';
import { AddWantedTerminals } from './AddWantedTerminals';

export const AddWantedTerminalsContainer = ({ wantedId }: AddWantedTerminalsContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();
  const { schema } = useAddWantedTerminalsValidation();
  const [selectedServerId, setSelectedServerId] = useState('');

  const methods = useForm<AddWantedTerminalsFormBody>({
    defaultValues: {
      idTkds: [],
    },
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const { data: serversResponse, isLoading: isLoadingServers } = useQuery([QueryKeyEnum.SERVERS], fetchServers, {
    onSuccess: (serversResponse) => {
      if (serversResponse.data.length) {
        setSelectedServerId(serversResponse.data[0].id);
      }
    },
  });

  const { data: terminalsResponse, isInitialLoading: isLoadingTerminals } = useQuery(
    [QueryKeyEnum.TERMINALS, selectedServerId],
    () => fetchTerminals(selectedServerId),
    {
      args: {
        zaakceptowane: true,
        sort: [WantedTerminalsIdSortEnum.ASC],
        wantedId,
        wyswietlacz: true,
      },
      enabled: !!selectedServerId,
    },
  );

  const { mutate: onAddWantedTerminals, isLoading } = useMutation(addWantedTerminals, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyEnum.WANTED_TERMINALS]);
      queryClient.invalidateQueries([QueryKeyEnum.TERMINALS, selectedServerId]);

      toast.dark(formatMessage({ id: AppMessages['wanted.terminals.add.terminal.success'] }));

      onModalClose();
    },
  });

  const onSubmit = (body: AddWantedTerminalsFormBody) => {
    onAddWantedTerminals({
      id: wantedId,
      body: body.idTkds.map(({ value }) => `${value}`),
    });
  };

  useEffect(() => {
    methods.reset();
  }, [isModalOpen, selectedServerId]);

  return (
    <FormProvider {...methods}>
      <AddWantedTerminals
        onSubmit={onSubmit}
        isModalOpen={isModalOpen}
        onModalOpen={onModalOpen}
        onModalClose={onModalClose}
        isLoading={isLoading}
        isLoadingTerminals={isLoadingTerminals}
        terminals={terminalsResponse?.data || []}
        selectedServerId={selectedServerId}
        servers={serversResponse?.data || []}
        setSelectedServerId={setSelectedServerId}
        isLoadingServers={isLoadingServers}
      />
    </FormProvider>
  );
};
